import React, { useState, useRef, useCallback, useEffect } from 'react';
import axios from 'axios';
import './App.css'; // Reuse styles if applicable

import FilterModal from './FilterModal';
import { use } from 'react';

const DataReviewSidebar = ({
    collections = [],        // List of available collection names
    selectedCollection,      // Currently selected collection name (controlled by parent)
    setSelectedCollection,   // Function to call when collection selection changes (passed from parent)
    knGraphOption,           // Current graph option ('kn_graph' or 'kn_graph_full')
    setKnGraphOption,        // Function to set graph option
    addFilesToList,          // Function to add search/directory results to the main list
    selectedFiles = [],      // Currently selected files in the main view (for context)
    isLoading: isParentLoading, // Renamed to avoid conflict with local isLoading/isSearching
}) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [searchError, setSearchError] = useState(null);
    const [searchMeta, setSearchMeta] = useState({ count: 0, query: '' }); // Store count and generated query

    const [appliedFilters, setAppliedFilters] = useState({});
    const [tempFilters, setTempFilters] = useState({});
    const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
    const [allColumns, setAllColumns] = useState([]); // Stores discoverable columns for filtering

    // const baseURL = '';
    const searchAbortControllerRef = useRef(null);

    // Fetch table columns for filtering upon load
    useEffect(() => {
        const fetchColumns = async () => {
            try {
                const response = await axios.get('/api/knowledge_table_columns', { withCredentials: true });
                if (response.data && Array.isArray(response.data)) {
                    console.log('Fetched columns:', response.data);
                    // Each row is likely an object with a column name property, e.g. { column_name: "foo" }
                    // Try to extract column names from the first key of each object
                    const columnNames = response.data.map(row => {
                        return row[0];
                    });

                    console.log('Column names:', columnNames);

                    const filterableColumns = columnNames.filter(columnNames => !columnNames.startsWith('kn_') && columnNames !== 'id' && columnNames !== 'directory');
                    setAllColumns(filterableColumns);
                } else {
                    console.error('Unexpected columns response format:', response.data);
                    setAllColumns([]);
                }
            } catch (error) {
                console.error('Error fetching columns:', error);
                setAllColumns([]);
            }
        };
        fetchColumns();
        console.log('Fetched columns:', allColumns);
    }, []);
    

    // Function to perform search API call - Uses /api/search_files
    const executeSearch = useCallback(async (currentSearchQuery, currentAppliedFilters) => {
        const query = currentSearchQuery.trim();
        const filtersPresent = Object.keys(currentAppliedFilters).length > 0;

        if (searchAbortControllerRef.current) {
            searchAbortControllerRef.current.abort();
        }

        if (!query && !filtersPresent) {
            setSearchResults([]);
            setSearchMeta({ count: 0, query: '' });
            setSearchError(null);
            setIsSearching(false); // Ensure searching state is reset
            return;
        }

        const controller = new AbortController();
        searchAbortControllerRef.current = controller;

        setIsSearching(true);
        setSearchError(null);

        try {
            const searchParams = {
                searchTerm: query,
                filters: JSON.stringify(currentAppliedFilters),
                // selectedDirectory: null, // Not used in this sidebar
            };
            const url = `/api/search_files`;
            const response = await axios.get(url, {
                params: searchParams,
                withCredentials: true,
                signal: controller.signal
            });

            if (response.data && Array.isArray(response.data.results)) {
                setSearchResults(response.data.results);
                setSearchMeta({
                    count: response.data.fileCount || 0,
                    query: response.data.query || ''
                });

                if (response.data.results.length === 0) {
                    setSearchError(null); // No error for zero results
                }
            } else {
                console.error('Unexpected search response format:', response.data);
                setSearchError('Received unexpected data format from search.');
                setSearchResults([]);
                setSearchMeta({ count: 0, query: '' });
            }
        } catch (error) {
            if (axios.isCancel(error)) {
                console.log(`Search aborted for query: "${query}"`);
            } else {
                console.error('Error searching files:', error);
                setSearchError(`Search failed: ${error.response?.data?.error || error.message}`);
                setSearchResults([]);
                setSearchMeta({ count: 0, query: '' });
            }
        } finally {
            if (searchAbortControllerRef.current === controller) {
                setIsSearching(false);
                searchAbortControllerRef.current = null;
            }
        }
    }, []);

    const handleSearchFormSubmit = (e) => {
        if (e) e.preventDefault();
        executeSearch(searchQuery, appliedFilters);
    };

    // Function to clear search results and query
    const clearSearch = useCallback(() => {
        if (searchAbortControllerRef.current) {
            searchAbortControllerRef.current.abort();
            searchAbortControllerRef.current = null;
        }
        setSearchQuery('');
        setSearchResults([]);
        setSearchMeta({ count: 0, query: '' });
        setSearchError(null);
        setAppliedFilters({});
        setIsSearching(false);
    }, []);

    // --- Filter Modal Handlers ---
    const handleOpenFilterModal = () => {
        setTempFilters({ ...appliedFilters });
        setIsFilterModalOpen(true);
    };

    const handleFilterChange = useCallback((column, value) => {
        setTempFilters(prev => ({ ...prev, [column]: value }));
    }, []);

    const handleApplyFiltersFromModal = useCallback(() => {
        const newAppliedFilters = { ...tempFilters };
        for (const key in newAppliedFilters) {
            if (newAppliedFilters[key] === '' || newAppliedFilters[key] === null) {
                delete newAppliedFilters[key];
            }
        }
        setAppliedFilters(newAppliedFilters);
        setIsFilterModalOpen(false);
        executeSearch(searchQuery, newAppliedFilters);
    }, [tempFilters, searchQuery, executeSearch]);

    const handleClearAllFiltersInModal = useCallback(() => {
        setTempFilters({});
        setAppliedFilters({});
        setIsFilterModalOpen(false);
        executeSearch(searchQuery, {});
    }, [searchQuery, executeSearch]);

    const handleClearAppliedFilterTag = useCallback((filterKey) => {
        const newAppliedFilters = { ...appliedFilters };
        delete newAppliedFilters[filterKey];
        setAppliedFilters(newAppliedFilters);
        executeSearch(searchQuery, newAppliedFilters);
    }, [appliedFilters, searchQuery, executeSearch]);

    // Get IDs of files already selected in the main view for highlighting/disabling add button
    const selectedFileIds = new Set(selectedFiles.map(f => f.id));
    const globalLoading = isParentLoading || isSearching;

    return (
        <div className="data-review-sidebar" style={{
            width: '350px', // Slightly wider to accommodate results better
            flexShrink: 0,
            borderRight: '1px solid var(--border-color)',
            padding: '15px',
            overflowY: 'auto',
            background: '#f8f9fa', // Light background for sidebar
            display: 'flex',
            flexDirection: 'column',
            gap: '20px' // Spacing between sections
        }}>
            {/* --- Search Section --- */}
            <div className="sidebar-section">
                <h3 style={{ marginTop: 0, marginBottom: '10px' }}>Search & Add Files</h3>
                <form onSubmit={handleSearchFormSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '10px' }}>
                    <div style={{ display: 'flex', gap: '5px' }}>
                        <input
                            type="text"
                            placeholder="LLM query..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            style={{ flexGrow: 1, padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                            disabled={globalLoading}
                        />
                        <button type="submit" disabled={globalLoading || (!searchQuery.trim() && Object.keys(appliedFilters).length === 0)}>
                            {isSearching ? '...' : 'Search'}
                        </button>
                    </div>
                    <button
                        type="button"
                        onClick={handleOpenFilterModal}
                        // disabled={allColumns.length === 0 || !allColumns}
                        title={!allColumns || allColumns.length === 0 ? "No filterable columns available" : "Open filters"}
                    >
                        Filters ({Object.keys(appliedFilters).length})
                    </button>
                </form>

                {/* Display Applied Filters */}
                {Object.keys(appliedFilters).length > 0 && (
                    <div className="applied-filters-display" style={{ marginBottom: '10px', padding: '8px', backgroundColor: '#e9ecef', borderRadius: '4px', display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                        <strong style={{ marginRight: '5px', fontSize: '0.9em' }}>Active Filters:</strong>
                        {Object.entries(appliedFilters).map(([key, value]) => (
                            <span key={key} className="filter-tag" style={{ backgroundColor: '#ced4da', padding: '3px 8px', borderRadius: '3px', fontSize: '0.85em' }}>
                                {key}: "{String(value)}"
                                <button onClick={() => handleClearAppliedFilterTag(key)} style={{ marginLeft: '5px', border: 'none', background: 'transparent', color: 'red', cursor: 'pointer', fontWeight: 'bold', padding: '0 2px' }}>x</button>
                            </span>
                        ))}
                         <button
                            onClick={() => { setAppliedFilters({}); executeSearch(searchQuery, {}); }}
                            style={{ marginLeft: 'auto', padding: '3px 8px', fontSize: '0.85em', lineHeight: '1.2' }}
                            className="secondary" // Assuming a secondary button style
                            disabled={globalLoading}
                        >
                            Clear All
                        </button>
                    </div>
                )}


                {isSearching && <div className="loading" style={{ fontSize: '0.9em', textAlign: 'center', padding: '5px' }}>Searching...</div>}
                {searchError && !isSearching && <div className="error" style={{ fontSize: '0.9em', padding: '5px', color: 'red' }}>{searchError}</div>}

                {!isSearching && searchMeta.query && (
                    <div style={{ marginBottom: '10px', background: '#eee', padding: '5px 8px', borderRadius: '3px' }}>
                        <span style={{ fontSize: '0.8em', color: '#555' }}>Executed Query:</span>
                        <pre style={{ fontSize: '0.75em', margin: '3px 0 0 0', maxHeight: '60px', overflowY: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                            {searchMeta.query}
                        </pre>
                    </div>
                )}

                {searchResults.length > 0 && (
                    <div className="search-results-section">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                            <h4 style={{ margin: 0, fontSize: '0.95em' }}>Results ({searchMeta.count}):</h4>
                            <div>
                                <button
                                    onClick={() => addFilesToList(searchResults)}
                                    style={{ fontSize: '0.8em', padding: '3px 6px', marginRight: '5px' }}
                                    disabled={globalLoading}
                                    title="Add all search results to main selection"
                                >
                                    Add All ({searchResults.length})
                                </button>
                                <button
                                    onClick={clearSearch}
                                    style={{ fontSize: '0.8em', padding: '3px 6px', background: '#6c757d', color: 'white', border: 'none', borderRadius: '3px' }}
                                    disabled={globalLoading}
                                >
                                    Clear Search & Filters
                                </button>
                            </div>
                        </div>
                        <ul style={{ listStyle: 'none', padding: 0, margin: 0, maxHeight: '250px', overflowY: 'auto', border: '1px solid #eee', borderRadius: '4px', background: '#fff' }}>
                            {searchResults.map((file) => (
                                <li key={file.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', borderBottom: '1px solid #eee' }}>
                                    <span style={{ fontSize: '0.9em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '200px' }} title={`${file.file_name || 'Unknown File'} (ID: ${file.id})`}>
                                        {file.file_name || `ID: ${file.id}`}
                                    </span>
                                    <button
                                        onClick={() => addFilesToList([file])}
                                        style={{ fontSize: '0.8em', padding: '2px 5px', flexShrink: 0, cursor: 'pointer' }}
                                        disabled={globalLoading || selectedFileIds.has(file.id)}
                                        title={selectedFileIds.has(file.id) ? "Already in selection" : "Add file to main selection"}
                                    >
                                        {selectedFileIds.has(file.id) ? 'Selected' : 'Add +'}
                                    </button>
                                </li>
                            ))}
                            {searchResults.length > 0 && (
                                <li style={{ padding: '5px 8px', background: '#f8f9fa', textAlign: 'center', fontSize: '0.85em', fontStyle: 'italic', color: '#666' }}>
                                    End of results
                                </li>
                            )}
                        </ul>
                    </div>
                )}
                {!isSearching && (searchQuery.trim() || Object.keys(appliedFilters).length > 0) && searchResults.length === 0 && searchMeta.count === 0 && !searchError && (
                    <p style={{ fontSize: '0.9em', fontStyle: 'italic', color: '#666', textAlign: 'center' }}>
                        No files found for your criteria.
                    </p>
                )}
            </div>

            {/* --- Collection Selection Section --- */}
            <div className="sidebar-section">
                <h3 style={{ marginTop: 0, marginBottom: '10px' }}>Load Collection</h3>
                <select
                    className="uniform-input" // Reuse existing styles if possible
                    value={selectedCollection}
                    // The onChange now directly calls the function passed from the parent (DataReview's handleCollectionChange)
                    onChange={(e) => setSelectedCollection(e.target.value)}
                    style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px', height: '38px' }}
                    disabled={isSearching}
                >
                    <option value="">-- Select to Load --</option>
                    {/* Sort collections alphabetically */}
                    {collections.slice().sort((a, b) => a.localeCompare(b)).map((collectionName, index) => (
                        <option key={index} value={collectionName}>
                            {collectionName}
                        </option>
                    ))}
                </select>
                 {selectedCollection && (
                    <p style={{ fontSize: '0.9em', marginTop: '8px', color: '#333' }}>
                        Loaded: <strong>{selectedCollection}</strong> ({selectedFiles.length} files)
                     </p>
                )}
            </div>

            {/* --- Graph Option Section --- */}
            <div className="sidebar-section">
                <h3 style={{ marginTop: 0, marginBottom: '10px' }}>Graph Data Source</h3>
                <select
                    className="uniform-input"
                    value={knGraphOption}
                    onChange={(e) => setKnGraphOption(e.target.value)}
                    style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px', height: '38px' }}
                    disabled={isSearching}
                >
                    <option value="kn_graph">Standard Graph (kn_graph)</option>
                    <option value="kn_graph_full">Full Graph (kn_graph_full)</option>
                    {/* Add more options if other graph fields exist */}
                </select>
            </div>

             {/* --- Selected Files Overview --- */}
            <div className="sidebar-section" style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minHeight: '150px', borderTop: '1px dashed #ccc', paddingTop: '15px' }}>
                <h3 style={{ marginTop: 0, marginBottom: '10px' }}>Current Selection ({selectedFiles.length})</h3>
                {selectedFiles.length > 0 ? (
                    <ul style={{ listStyle: 'none', padding: '5px', margin: 0, flexGrow: 1, overflowY: 'auto', border: '1px solid #ccc', borderRadius: '4px', background: '#fff' }}>
                        {selectedFiles.map((file) => (
                            <li key={file.id} style={{ padding: '5px 8px', borderBottom: '1px solid #eee', fontSize: '0.9em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={file.file_name || `ID: ${file.id}`}>
                                {file.file_name || `ID: ${file.id}`}
                                {/* Could add a 'remove' button here if needed */}
                            </li>
                        ))}
                         <li style={{ padding: '5px 8px', background: '#f8f9fa', textAlign: 'center', fontSize: '0.85em', fontStyle: 'italic', color: '#666' }}>
                                End of list
                         </li>
                    </ul>
                ) : (
                    <p style={{ fontSize: '0.9em', fontStyle: 'italic', color: '#666', textAlign: 'center', margin: 'auto 0' }}>
                        Selection is empty.
                        <br/>
                        Use search or load a collection.
                    </p>
                )}
            </div>

            <FilterModal
                isOpen={isFilterModalOpen}
                onClose={() => setIsFilterModalOpen(false)}
                allColumns={allColumns}
                currentFilters={tempFilters}
                onApplyFilters={handleApplyFiltersFromModal}
                onFilterChange={handleFilterChange}
                onClearAllFilters={handleClearAllFiltersInModal}
            />

        </div>
    );
};

export default DataReviewSidebar;
